import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  MapPin,
  Clock,
  Utensils,
  Star,
  Calendar,
  ChevronRight,
  Tag,
  ArrowLeft,
} from "lucide-react";
import Seo from "../components/Seo";
import RetreatInquiryForm from "../components/RetreatInquiryForm";
import { retreatSlug, resolveBasePriceInr } from "../lib/retreatSlug";
import { buildRetreatJsonLd } from "../lib/retreatJsonLd";
import { getDisplayableBatches, getBatchPrices } from "../lib/batchDisplay";
import { useCurrency } from "../context/CurrencyContext";
import { formatFromInr } from "../lib/currency";
import { SUPPORTED_CURRENCIES } from "../lib/currencyConfig";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

function formatSourceLabel(value) {
  if (!value) return "";
  const raw = String(value).trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function getImageSrc(retreat) {
  if (retreat?.source === "realhappiness") return "/images/realhappiness.jpg";
  if (retreat?.source === "patanjaliyoga") return "/images/patanjali_yoga_foundation.jpg";
  if (retreat?.thumbnail_url) {
    const raw = String(retreat.thumbnail_url).trim().replace(/^\/+/, "");
    const p = raw.startsWith("images/") ? raw : `images/${raw}`;
    return `/${p}`;
  }
  return "/images/nalanda.jpg";
}

/** @deprecated Use retreatSlug from ../lib/retreatSlug */
export function retreatToSlug(retreat) {
  return retreatSlug(retreat);
}

function normalizeRetreat(item) {
  return {
    ...item,
    batches: Array.isArray(item.batches)
      ? item.batches
      : typeof item.batches === "string"
      ? JSON.parse(item.batches)
      : [],
    styles: item.style_tags
      ? item.style_tags.split(",").map((s) => s.trim()).filter(Boolean)
      : item.style
      ? item.style.split(",").map((s) => s.trim()).filter(Boolean)
      : [],
    types: item.type_tags
      ? item.type_tags.split(",").map((t) => t.trim()).filter(Boolean)
      : item.type
      ? item.type.split(",").map((t) => t.trim()).filter(Boolean)
      : [],
  };
}

function RetreatDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Support legacy ?key= URLs: redirect them to the clean slug URL.
  const keyParam = new URLSearchParams(location.search).get("key");

  const [retreat, setRetreat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);
  const { currency, setCurrency, rates } = useCurrency();

  const formatPrice = (amountInr) => formatFromInr(amountInr, currency, rates);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (keyParam) {
      // Old ?key= URL — fetch by retreat_key then redirect to the clean slug URL.
      axios
        .get(`${API_URL}/retreats/${encodeURIComponent(keyParam)}`)
        .then((res) => {
          const cleanSlug = res.data.slug || retreatSlug(res.data);
          navigate(`/retreats/${cleanSlug}`, { replace: true });
        })
        .catch(() => {
          setError("Could not load retreat details. Please try again.");
          setLoading(false);
        });
      return;
    }

    if (!slug) {
      setError("Retreat not found.");
      setLoading(false);
      return;
    }

    // Normal clean-URL path — fetch by slug.
    axios
      .get(`${API_URL}/retreats/slug/${encodeURIComponent(slug)}`)
      .then((res) => setRetreat(normalizeRetreat(res.data)))
      .catch(() => setError("Could not load retreat details. Please try again."))
      .finally(() => setLoading(false));
  }, [slug, keyParam, navigate]);

  // Fetch a few other retreats in the same location for internal linking
  // (improves "only one incoming internal link" and crawl depth for SEO).
  useEffect(() => {
    if (!retreat || !retreat.location_tags) {
      setRelated([]);
      return;
    }
    const loc = retreat.location_tags.split(",")[0].trim();
    axios
      .get(`${API_URL}/search`, { params: { location: loc, limit: 7 } })
      .then((res) => {
        const items = (res.data?.items || [])
          .filter((r) => r.retreat_key && retreatSlug(r) !== slug)
          .slice(0, 6);
        setRelated(items);
      })
      .catch(() => setRelated([]));
  }, [retreat, slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading retreat details...</p>
        </div>
      </div>
    );
  }

  if (error || !retreat) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-gray-600 mb-4">{error || "Retreat not found."}</p>
          <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 justify-center">
            <ArrowLeft size={16} /> Back to all retreats
          </Link>
        </div>
      </div>
    );
  }

  const pageTitle = retreat.retreat_name || "Yoga Retreat";
  // Always lead with the unique retreat name + location so no two retreat
  // descriptions are identical (fixes SEMrush "duplicate meta descriptions").
  const pageDescription = (() => {
    const where = retreat.location
      ? retreat.location.split(",")[0].trim()
      : "India";
    const lead = `${pageTitle} in ${where}.`;
    const body = retreat.description
      ? ` ${retreat.description.replace(/\s+/g, " ").trim()}`
      : ` Compare dates, prices, meals, ratings and reviews on Yoga Retreats Booking.`;
    return lead + body;
  })();
  const canonicalPath = `/retreats/${slug}`;
  const imageSrc = getImageSrc(retreat);


  const jsonLd = buildRetreatJsonLd(retreat, { canonicalPath, imageSrc });
  const displayableBatches = getDisplayableBatches(retreat.batches, currency, formatPrice);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalPath={canonicalPath}
        ogImagePath={imageSrc.startsWith("http") ? imageSrc : imageSrc}
        jsonLd={jsonLd}
      />

      {/* Hero image */}
      <div className="relative h-72 md:h-96 bg-gray-900 overflow-hidden">
        <img
          src={imageSrc}
          alt={pageTitle}
          className="w-full h-full object-cover opacity-70"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={(e) => {
            e.target.src =
              retreat?.source === "patanjaliyoga"
                ? "/images/patanjali_yoga_foundation.jpg"
                : "/images/nalanda.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-3 transition-colors"
            >
              <ArrowLeft size={14} /> All retreats
            </Link>
            <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-2">{pageTitle}</h1>
            {retreat.location && (
              <p className="flex items-center gap-1.5 text-gray-300 text-sm font-medium">
                <MapPin size={14} className="text-emerald-400" />
                {retreat.location}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Quick facts */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {retreat.duration && (
                  <div className="flex items-start gap-2">
                    <Clock size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Duration</p>
                      <p className="text-sm font-semibold text-gray-800">{retreat.duration}</p>
                    </div>
                  </div>
                )}
                {retreat.meals && retreat.meals.toLowerCase() !== "n/a" && (
                  <div className="flex items-start gap-2">
                    <Utensils size={18} className="text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Meals</p>
                      <p className="text-sm font-semibold text-gray-800">{retreat.meals}</p>
                    </div>
                  </div>
                )}
                {retreat.google_rating && (
                  <div className="flex items-start gap-2">
                    <Star size={18} className="text-yellow-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Google Rating</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {retreat.google_rating}
                        {retreat.google_review_count && (
                          <span className="text-gray-400 font-normal"> ({retreat.google_review_count} reviews)</span>
                        )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Style + type tags */}
            {(retreat.styles?.length > 0 || retreat.types?.length > 0) && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Tag size={14} /> Style & Type
                </h2>
                <div className="flex flex-wrap gap-2">
                  {retreat.types?.map((t, i) => (
                    <span key={i} className="bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100">
                      {t}
                    </span>
                  ))}
                  {retreat.styles?.map((s, i) => (
                    <span key={i} className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full border border-emerald-100">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {retreat.description && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 mb-3">About this retreat</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">{retreat.description}</p>
              </div>
            )}

            {/* Batches */}
            {displayableBatches.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Calendar size={18} className="text-blue-500" /> Available Dates & Prices
                  </h2>
                  <div className="flex items-center gap-2">
                    <label htmlFor="batch-currency" className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                      Currency
                    </label>
                    <select
                      id="batch-currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    >
                      {SUPPORTED_CURRENCIES.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-3">
                  {displayableBatches.map((batch, idx) => {
                    const { sharedPrice, privatePrice } = getBatchPrices(batch, currency, formatPrice);
                    const dateLabel = (batch.course_date || "").toString().trim();

                    return (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="flex items-center gap-2">
                          <Calendar size={15} className="text-blue-400 shrink-0" />
                          <span className="text-sm font-medium text-gray-700">{dateLabel}</span>
                          {batch.availability && !String(batch.availability).toLowerCase().includes("check website") && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${batch.availability.toLowerCase().includes("fully") ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}>
                              {batch.availability}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 text-xs text-gray-600">
                          {sharedPrice && <span className="font-semibold">Shared: {sharedPrice}</span>}
                          {privatePrice && <span className="font-semibold">Private: {privatePrice}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — location links */}
          <div className="space-y-4">
            {retreat.location_tags && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Location</p>
                {retreat.location_tags.split(",").map((tag, i) => {
                  const t = tag.trim();
                  const locSlug = t.toLowerCase().replace(/\s+/g, "-");
                  return (
                    <Link
                      key={i}
                      to={`/yoga-retreats-in/${locSlug}`}
                      className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors mb-1"
                    >
                      <MapPin size={13} /> More retreats in {t} <ChevronRight size={13} />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Full-width booking section — standard layout on retreat sites */}
        <section className="mt-12 scroll-mt-28 relative">
          <div id="enquire" className="absolute -top-28" aria-hidden="true" />
          <div id="book-enquire" className="absolute -top-28" aria-hidden="true" />
          <RetreatInquiryForm
            variant="section"
            retreat={{
              retreat_key: retreat.retreat_key,
              retreat_name: retreat.retreat_name,
              slug: retreat.slug || slug,
              location: retreat.location,
              source_url: retreat.source_url,
              price_inr: resolveBasePriceInr(retreat, rates),
            }}
          />
        </section>

        {/* Related retreats — internal linking for SEO */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              More Yoga Retreats
              {retreat.location ? ` in ${retreat.location.split(",")[0].trim()}` : " in India"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.retreat_key}
                  to={`/retreats/${retreatSlug(r)}`}
                  className="block bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all"
                >
                  <span className="block text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                    {r.retreat_name}
                  </span>
                  {r.location && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPin size={12} className="text-emerald-500" />
                      {r.location}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default RetreatDetailPage;
